/**
 * Session cache of users looked up BY ID — so surfaces that only need a few
 * names (participants table, issues, execution reports, rule text…) no longer
 * download the whole org's user set.
 *
 * - Requests made in the same tick are batched into one REST `batchGet`
 *   (chunked), and an id is never requested twice while in flight.
 * - Entries are `Doc<IUser>` or `null` (deleted / not readable). A failed
 *   lookup resolves its ids to `null` rather than wedging callers in "loading".
 * - Subscribers are told which ids resolved, so a hook re-renders only when an
 *   id IT asked about arrives.
 *
 * Pure module (no dependency on services/user) so services/user can wire it to
 * the real transport and invalidate it on refresh.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IUser } from '@dadohr/shared/domainTypes/user';

type Resolved = (ids: string[]) => void;
export type FetchUsersByIds = (ids: string[]) => Promise<Record<string, Doc<IUser> | null>>;

export const createUserIdCache = (fetchByIds: FetchUsersByIds) => {
  const entries = new Map<string, Doc<IUser> | null>();
  const inFlight = new Set<string>();
  const subscribers = new Set<Resolved>();
  let queued = new Set<string>();
  let flushScheduled = false;

  const emit = (ids: string[]) => {
    if (!ids.length) return;
    Array.from(subscribers).forEach((s) => s(ids));
  };

  const flush = () => {
    flushScheduled = false;
    const ids = Array.from(queued);
    queued = new Set();
    if (!ids.length) return;
    ids.forEach((id) => inFlight.add(id));
    fetchByIds(ids)
      .then(
        (found) => {
          ids.forEach((id) => entries.set(id, found[id] ?? null));
        },
        (err) => {
          // eslint-disable-next-line no-console
          console.warn('user id lookup failed; showing as unknown', err);
          ids.forEach((id) => entries.set(id, null));
        }
      )
      .finally(() => {
        ids.forEach((id) => inFlight.delete(id));
        emit(ids);
      });
  };

  return {
    /** Cached entry: Doc, null (known missing), or undefined (not looked up). */
    get: (id: string): Doc<IUser> | null | undefined => entries.get(id),
    has: (id: string) => entries.has(id),
    /** Queue lookups for ids not cached and not in flight (batched per tick). */
    request: (ids: string[]) => {
      let added = false;
      ids.forEach((id) => {
        if (!id || entries.has(id) || inFlight.has(id) || queued.has(id)) return;
        queued.add(id);
        added = true;
      });
      if (added && !flushScheduled) {
        flushScheduled = true;
        Promise.resolve().then(flush);
      }
    },
    subscribe: (fn: Resolved) => {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },
    /**
     * Drop cached entries for an org (and every known-missing entry, which may
     * have been created since) so the next lookup re-reads them.
     */
    invalidateOrg: (orgId: string) => {
      const dropped: string[] = [];
      entries.forEach((d, id) => {
        if (d === null || d.data.orgId === orgId) {
          entries.delete(id);
          dropped.push(id);
        }
      });
      emit(dropped);
    }
  };
};

export type UserIdCache = ReturnType<typeof createUserIdCache>;
